/**
 * UserSignup - creating an account for someone who POSTS chores
 * -------------------------------------------------------------
 * After signing up they land on the membership page, because posting a chore
 * needs an active €5/year membership.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import LocationFields from '../components/LocationFields';
import TermsCheckbox from '../components/TermsCheckbox';
import SchoolFields from '../components/SchoolFields';
import { markNotificationPromptDue } from '../components/NotificationPrompt';
import { authApi } from '../services/api';

import { useI18n } from '../hooks/useI18n';
import PasswordInput from '../components/PasswordInput';
import { loadDraft, saveDraft, clearDraft, POSTER_SIGNUP_DRAFT } from '../utils/draft';

export default function UserSignup() {
  const { t } = useI18n();
  const { registerUser, subscriptionsRequired } = useAuth();
  const navigate = useNavigate();

  /**
   * Whatever was typed before going off to read the terms - see
   * utils/draft.js. Read once as the screen opens, not on every keystroke.
   */
  const draft = loadDraft(POSTER_SIGNUP_DRAFT);

  const [form, setForm] = useState(() => ({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    schoolCode: '',
    ...(draft?.form || {}),
    // A draft saved by the older page, which had a school box, may carry
    // one. The server ignores it; leaving it out keeps it from being sent.
    school: undefined,
  }));
  // Whether posters must enter the joining code is a server setting, so ask.
  const [posterCodeRequired, setPosterCodeRequired] = useState(false);

  useEffect(() => {
    authApi
      .schools()
      .then((d) => setPosterCodeRequired(Boolean(d.posterCodeRequired)))
      .catch(() => setPosterCodeRequired(false));
  }, []);
  const [location, setLocation] = useState(
    () => draft?.location || { city: '', address: '', lat: null, lng: null }
  );
  /**
   * Not restored, on purpose. Everything else on this form is work the person
   * did; this is them saying they have read something, and the site must not
   * remember that for them.
   */
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  // Keep the draft in step, so the work survives any way of leaving this
  // page - the terms link, the back button, or an accidental reload.
  useEffect(() => {
    saveDraft(POSTER_SIGNUP_DRAFT, { form, location });
  }, [form, location]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  /** Finds the message for one field, if the server complained about it. */
  const errorFor = (field) => fieldErrors.find((e) => e.field === field)?.message;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setFieldErrors([]);
    setBusy(true);

    try {
      await registerUser({ ...form, location, acceptedTerms });
      // The account exists - the half-finished copy of it, password included,
      // has no reason to stay in the tab.
      clearDraft(POSTER_SIGNUP_DRAFT);
      // Ask about notifications, and show the home-screen steps, once - on the
      // dashboard they are about to land on. Posters were never offered
      // either, so a chore could sit with an application on it for days
      // because nothing told anybody.
      markNotificationPromptDue();
      // Land on their own dashboard, not the "post a chore" form. Arriving
      // straight into an empty form gives someone no sense of where they are
      // or what the site does, and no obvious way out of it. The dashboard has
      // a "Post a chore" button in plain sight for anyone who came to do that.
      //
      // While the site charges, the membership page still comes first: there
      // is no point showing a dashboard they cannot use yet.
      navigate(subscriptionsRequired ? '/subscribe' : '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.fieldErrors || []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="page-header text-center">
        <h1>{t('userSignup.title')}</h1>
        <p>
          {subscriptionsRequired
            ? t('userSignup.subtitlePaid')
            : t('userSignup.subtitleFree')}
        </p>
      </div>

      <div className="card">
        <form className="form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <div>
                {error}
                {fieldErrors.length > 0 && (
                  <ul>
                    {fieldErrors.map((e) => (
                      <li key={e.field}>{e.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="firstName">{t('form.firstName')} *</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={form.firstName}
              onChange={handleChange}
              autoComplete="given-name"
              aria-invalid={Boolean(errorFor('firstName'))}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="lastName">{t('form.lastName')} *</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={form.lastName}
              onChange={handleChange}
              autoComplete="family-name"
              aria-invalid={Boolean(errorFor('lastName'))}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="email">{t('form.email')} *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              aria-invalid={Boolean(errorFor('email'))}
              required
            />
            {errorFor('email') && <span className="field-error">{errorFor('email')}</span>}
          </div>

          <div className="field">
            <label htmlFor="password">{t('form.password')} *</label>
            <PasswordInput
              id="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              aria-invalid={Boolean(errorFor('password'))}
              required
            />
            <span className="hint">{t('form.passwordRule')}</span>
            {errorFor('password') && <span className="field-error">{errorFor('password')}</span>}
          </div>

          <div className="field">
            <label htmlFor="phone">{t('form.phone')}</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
              placeholder={t('form.optional')}
            />
          </div>

          {/* No school here - people posting chores are not students. What
              places a poster is their town, just below. The invite code only
              appears if the site is set up to ask posters for it. */}
          <SchoolFields
            askSchool={false}
            schoolCode={form.schoolCode}
            onCodeChange={(v) => setForm((f) => ({ ...f, schoolCode: v }))}
            requireCode={posterCodeRequired}
            codeError={errorFor('schoolCode')}
          />

          <LocationFields value={location} onChange={setLocation} />

          <hr className="divider" />

          <TermsCheckbox
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
            error={errorFor('acceptedTerms')}
          />

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={busy || !acceptedTerms}
          >
            {busy ? t('userSignup.creating') : t('userSignup.create')}
          </button>
        </form>

        <hr className="divider" />

        <p className="text-center text-sm text-muted">
          {t('userSignup.areYouStudent')} <Link to="/signup/student">{t('userSignup.signUpHere')}</Link>
          <br />
          {t('userSignup.registered')} <Link to="/login">{t('nav.logIn')}</Link>
        </p>
      </div>
    </div>
  );
}
